import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';

/**
 * AuthCallback Component
 * Handles the OAuth callback from Emergent Auth (Google SSO)
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuthCallback = async () => {
      try {
        // Extract session_id from URL hash
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          toast.error('Authentication failed - no session ID received');
          navigate('/login', { replace: true });
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Exchange session_id for JWT token via backend
        const response = await api.post('/auth/google/session', { 
          session_id: sessionId 
        });

        if (response.data.token) {
          // Use loginWithToken to set up auth state
          await loginWithToken(response.data.token);
          toast.success('Signed in with Google successfully!');
          
          // Clear the hash and navigate to dashboard
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { replace: true });
        } else {
          throw new Error('No token received from server');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error(error.response?.data?.detail || 'Authentication failed. Please try again.');
        navigate('/login', { replace: true });
      }
    };

    processAuthCallback();
  }, [navigate, loginWithToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-lg text-muted-foreground">Completing sign in...</p>
        <p className="text-sm text-muted-foreground mt-2">Please wait while we verify your account</p>
      </div>
    </div>
  );
};

export default AuthCallback;
